import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Clock, Plus, Edit, Trash2, Save, ArrowLeft, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import Sidebar from '../../GlobalManagement/Pages/Sidebar';
import SearchBar from '../../GlobalManagement/Pages/SearchBar';
import { 
  masterBodyArea, 
  masterContentArea, 
  masterPageShell, 
  masterHeader, 
  masterHeaderCard, 
  masterHeaderInfo, 
  masterHeaderIcon, 
  masterTitle, 
  masterAccentButton, 
  masterPrimaryButton, 
  masterTableBody, 
  masterPanel, 
  masterTable, 
  masterStickyHeadRow, 
  masterTableData, 
  masterTbody, 
  masterTableRow, 
  masterTableCell, 
  masterEditButton, 
  masterDeleteButton, 
  masterFormContainer, 
  masterFormSectionHeader, 
  masterFormGrid2, 
  masterFormGridRow, 
  masterInputLabel, 
  masterInput, 
  masterSelect, 
  masterFormActionBlock, 
  masterActiveColor, 
  masterInactiveColor, 
  masterActionButtonsContainer,
  masterReadOnly,
  masterTableDatastatus,
  masterTableCellstatus
} from '../../GlobalManagement/Pages/masterPageStyles';

const ShiftMaster = () => {
  const [shifts, setShifts] = useState([]);
  const [showForm, setShowForm] = useState(false); // Toggle between List view and Form view
  const [isSaving, setIsSaving] = useState(false);
  const [editId, setEditId] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(18);

  const [shift, setShift] = useState({
    shiftID: "",
    shiftCode: "",
    shiftName: "",
    startTime: "",
    endTime: "",
    breakDuration: "",
    status: "Active",
  });

  const to12Hour = (time24) => {
    if (!time24) return "";
    let [h, m] = time24.split(":").map(Number);
    const ampm = h >= 12 ? " PM" : " AM";
    h = h % 12 || 12;
    return `${String(h).padStart(2, "0")}.${String(m).padStart(2, "0")}${ampm}`;
  };

  const to24Hour = (time12) => {
    if (!time12) return "";
    const [time, mod] = time12.split(" ");
    if (!time || !mod) return time12;
    let [h, m] = time.split(".").map(Number);

    if (mod === "PM" && h !== 12) h += 12;
    if (mod === "AM" && h === 12) h = 0;

    return `${String(h || 0).padStart(2, "0")}:${String(m || 0).padStart(2, "0")}`;
  };

  const fetchShifts = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("/api/shifts", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShifts(res.data || []);
    } catch (err) {
      toast.error("Failed to load shifts");
    }
  };

  const fetchNextShiftId = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("/api/shifts/next-id", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const nextId = res.data?.shiftID || "SHIFT0001";
      setShift((prev) => ({ ...prev, shiftID: nextId }));
    } catch (err) {
      setShift((prev) => ({ ...prev, shiftID: "" }));
    }
  };

  useEffect(() => {
    fetchShifts();
  }, []);

  const filteredData = useMemo(() => {
    const sortedList = [...shifts].sort((a, b) => {
      const idA = String(a.shiftID || a._id || '');
      const idB = String(b.shiftID || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((item) => {
      const name = (item.shiftName || '').toLowerCase();
      const code = (item.shiftCode || '').toLowerCase();
      const shiftId = (item.shiftID || '').toLowerCase();
      return name.startsWith(q) || code.startsWith(q) || shiftId.startsWith(q);
    });
  }, [shifts, searchTerm]);

  const paginatedData = useMemo(() => {
    if (pageSize === -1) return filteredData;
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setShift((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddNew = async () => {
    setEditId(null);
    setShift({
      shiftID: "",
      shiftCode: "",
      shiftName: "",
      startTime: "",
      endTime: "",
      breakDuration: "",
      status: "Active",
    });
    setShowForm(true);
    await fetchNextShiftId();
  };

  const handleEdit = (s) => {
    setEditId(s._id);
    setShift({
      shiftID: s.shiftID || "",
      shiftCode: s.shiftCode || "",
      shiftName: s.shiftName || "",
      startTime: to24Hour(s.startTime) || "",
      endTime: to24Hour(s.endTime) || "",
      breakDuration: s.breakDuration || "",
      status: s.status || "Active",
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this shift?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/shifts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Shift deleted successfully!");
      fetchShifts();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const handleSaveOrUpdate = async (e) => {
    e.preventDefault();
    if (!shift.shiftName.trim() || !shift.startTime || !shift.endTime) {
      toast.error("Shift name, start time, and end time are required");
      return;
    }

    setIsSaving(true);
    const token = localStorage.getItem("token");
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      const payload = {
        shiftID: shift.shiftID,
        shiftCode: shift.shiftCode.trim().toUpperCase(),
        shiftName: shift.shiftName.trim().toUpperCase(),
        startTime: to12Hour(shift.startTime),
        endTime: to12Hour(shift.endTime),
        breakDuration: shift.breakDuration,
        status: shift.status,
      };

      if (editId) {
        await axios.put(`/api/shifts/${editId}`, payload, config);
        toast.success("Shift updated successfully!");
      } else {
        await axios.post("/api/shifts", payload, config);
        toast.success("Shift added successfully!");
      }

      fetchShifts();
      setShowForm(false);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Error saving shift");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={masterBodyArea}>
      <Sidebar />
      <div className={`${masterPageShell} ${masterContentArea}`}>
        {/* Header Section */}
        <header className={masterHeader}>
          <div className={masterHeaderCard}>
            <div className={masterHeaderInfo}>
              <div className={masterHeaderIcon}><Clock size={14} /></div>
              <div><h1 className={masterTitle}>Shift Master</h1></div>
            </div>
            <button 
              type="button"
              onClick={showForm ? () => setShowForm(false) : handleAddNew} 
              className={showForm ? masterPrimaryButton : masterAccentButton}
            >
              {showForm ? <><ArrowLeft size={10} /> VIEW LIST</> : <><Plus size={10} /> ADD SHIFT</>}
            </button>
          </div>
        </header>

        <main className={`${masterTableBody} mt-1 w-full`}>
          {!showForm ? (
            <div className={`${masterPanel} flex flex-col overflow-hidden w-full`}>
              {/* Search & Pagination Toolbar */}
              <div className="border-b border-slate-200 flex-shrink-0 bg-white p-1 sm:p-2">
                <SearchBar 
                  value={searchTerm} 
                  onChange={(val) => {
                    setSearchTerm(val);
                    setCurrentPage(1);
                  }} 
                  placeholder="Search by shift name, code or ID..." 
                  totalItems={filteredData.length} 
                  currentPage={currentPage} 
                  pageSize={pageSize} 
                  onPageChange={setCurrentPage} 
                  onPageSizeChange={setPageSize} 
                />
              </div>

              {/* Horizontally Scrollable Table Container */}
              <div className="overflow-x-auto w-full">
                <table className={masterTable}>
                  <thead>
                    <tr className={masterStickyHeadRow}>
                      <th className={masterTableData} style={{ width: '60px' }}>S.No</th>
                      
                      <th className={masterTableData}>Shift Name</th>
                      <th className={masterTableData}>Code</th>
                      <th className={masterTableData}>Start Time</th>
                      <th className={masterTableData}>End Time</th>
                      <th className={masterTableData}>Break (Min)</th>
                      <th className={masterTableDatastatus}>Status</th>
                      <th className={masterTableDatastatus}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={masterTbody}>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((s, index) => (
                        <tr key={s._id} className={masterTableRow}>
                          <td className={masterTableCell}>
                            {pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1}
                          </td>
                          
                          <td className={masterTableCell} style={{ textAlign: 'left', fontWeight: '600' }}>
                            {s.shiftName}
                          </td>
                          <td className={masterTableCell}>
                            {s.shiftCode}
                          </td>
                          <td className={masterTableCell}>
                            {s.startTime}
                          </td>
                          <td className={masterTableCell}>
                            {s.endTime}
                          </td>
                          <td className={masterTableCell}>
                            {s.breakDuration || 0}
                          </td>
                          <td className={masterTableCellstatus}>
                            <span className={s.status === 'Active' ? masterActiveColor : masterInactiveColor}>
                              {s.status || 'Active'}
                            </span>
                          </td>
                          <td className={masterTableCellstatus}>
                            <div className={masterActionButtonsContainer}>
                              <button 
                                type="button" 
                                onClick={() => handleEdit(s)} 
                                className={masterEditButton}
                                title="Edit"
                              >
                                <Edit size={10} />
                              </button>
                              <button 
                                type="button" 
                                onClick={() => handleDelete(s._id)} 
                                className={masterDeleteButton}
                                title="Delete"
                              >
                                <Trash2 size={10} /> 
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="p-12 text-center text-slate-300 font-bold uppercase text-xs">
                          No shifts found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            /* Add / Edit Form Card */
            <form onSubmit={handleSaveOrUpdate} className={masterFormContainer}>
              <div className={masterFormSectionHeader}>
                <h2>{editId ? 'Edit Shift Details' : 'Create New Shift'}</h2>
              </div>

              <div className={masterFormGrid2}>
            

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Shift Name <span className="text-rose-500">*</span></label>
                  <input
                    type="text"
                    name="shiftName"
                    required
                    autoFocus
                    value={shift.shiftName}
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: e.target.name,
                          value: e.target.value.toUpperCase(),
                        },
                      })
                    }
                    className={masterInput}
                    placeholder="eg-MORNING, NIGHT, GENERAL"
                  />
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Shift Code <span className="text-rose-500">*</span></label>
                  <input
                    type="text"
                    name="shiftCode"
                    value={shift.shiftCode}
                    onChange={(e) => setShift({...shift, shiftCode: e.target.value.toUpperCase()})}
                    placeholder="eg: M, N, E"
                    required
                    maxLength={5}
                    className={masterInput}
                  />
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Start Time <span className="text-rose-500">*</span></label>
                  <input
                    type="time"
                    name="startTime"
                    value={shift.startTime}
                    onChange={handleChange}
                    className={masterInput}
                    required
                  />
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>End Time <span className="text-rose-500">*</span></label>
                  <input
                    type="time"
                    name="endTime"
                    value={shift.endTime}
                    onChange={handleChange}
                    className={masterInput}
                    required
                  />
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Break Duration (Minutes)</label>
                  <input
                    type="number"
                    name="breakDuration"
                    value={shift.breakDuration}
                    onChange={handleChange}
                    className={masterInput}
                    placeholder="e.g. 30"
                  />
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Status</label>
                  <select
                    name="status"
                    value={shift.status}
                    onChange={handleChange}
                    className={masterSelect}
                    required
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className={masterFormActionBlock}>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-3 py-1 rounded border border-slate-300 text-slate-700 font-semibold text-[11px] bg-white hover:bg-slate-50 transition-all uppercase"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className={masterAccentButton}
                >
                  {isSaving ? (
                    <Loader2 className="animate-spin" size={10} />
                  ) : (
                    <Save size={10} />
                  )}
                  {editId ? 'UPDATE SHIFT' : 'SAVE SHIFT'}
                </button>
              </div>
            </form>
          )}
        </main>
      </div>
    </div>
  );
};

export default ShiftMaster;